import { ColorContext } from 'App';
import { Slider } from 'antd';
import AlertIcons from 'components/AlertIcons';
import LabeledSelect from 'components/LabeledSelect';
import React, { useContext, useEffect } from 'react';
import { alertColorScale, getSeverityLevel } from 'utils/alertUtils';
import { GetDeviceTypeString } from 'utils/stringUtils';
import RadialAlertMap from '../RadialAlertMap';

export function VerifyRadialAlert(dataObj) {
  if (!dataObj) return false;
  return !(
    dataObj?.headline === '' ||
    dataObj?.description === '' ||
    dataObj?.type === '' ||
    dataObj?.radius === '' ||
    dataObj?.severity === ''
  );
}

export default function CreateRadialAlert({ onUpdate, deviceList, dataObj }) {
  const theme = useContext(ColorContext);

  useEffect(() => {
    if (
      !dataObj?.headline ||
      !dataObj?.description ||
      !dataObj?.type ||
      !dataObj?.radius ||
      !dataObj?.severity
    ) {
      onUpdate({
        headline: dataObj?.headline ?? '',
        description: dataObj?.description ?? '',
        type: dataObj?.type ?? '',
        radius: dataObj?.radius ?? 250,
        severity: dataObj?.severity ?? 25,
      });
    }
  }, []);

  return (
    <div className="container-fluid p-3 rounded-2 m-0 bg-primary">
      <div className="row g-3 align-items-stretch">
        {/* Map */}
        <div className="col-12 col-md-6 d-flex">
          <div
            className="flex-fill rounded-2 shadow overflow-hidden"
            style={{ height: 'clamp(280px, 50vh, 600px)' }}
          >
            <RadialAlertMap
              arrayOfDevices={deviceList}
              selectedDevice={dataObj.selectedDevice}
              setSelectedDevice={(nodeId) => {
                const device = deviceList.find((d) => d.node_id === nodeId);
                if (device) {
                  const deviceCopy = { ...device, data: [device.data[0]] };
                  onUpdate({
                    node_id: nodeId,
                    selectedDevice: deviceCopy,
                    coordinates: [device.lat, device.lon],
                  });
                }
              }}
              radius={Number(dataObj.radius) || 0}
            />
          </div>
        </div>

        {/* Form */}
        <div className="col-12 col-md-6 d-flex">
          <div className="flex-fill d-flex flex-column gap-3">
            {/* Device Alias */}
            <LabeledSelect
              label="Device Alias:"
              value={dataObj.node_id || ''}
              onChange={(e) => {
                const nodeId = e.target.value;
                const device = deviceList.find((d) => d.node_id.toString() === nodeId);
                const deviceCopy = device ? { ...device, data: [device.data[0]] } : null;
                if (device) {
                  onUpdate({
                    node_id: nodeId,
                    selectedDevice: deviceCopy,
                    coordinates: [device.lat, device.lon],
                  });
                } else {
                  onUpdate({ node_id: '', selectedDevice: null });
                }
              }}
              className="w-100"
            >
              <option value="">-Select-</option>
              {deviceList.map((device) => {
                if (GetDeviceTypeString(device) === dataObj.deviceType) {
                  const label =
                    (device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
                    device.alias;
                  return (
                    <option key={device.node_id} value={device.node_id}>
                      {label}
                    </option>
                  );
                }
                return null;
              })}
            </LabeledSelect>

            {/* Headline */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Headline:</div>
              <input
                type="text"
                name="headline"
                className="bg-base p-1 rounded-1 w-100"
                onChange={(e) => onUpdate({ headline: e.target.value })}
                value={dataObj.headline || ''}
              />
            </div>

            {/* Description */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Description:</div>
              <textarea
                name="description"
                className="bg-base p-1 rounded-1 w-100"
                rows={4}
                onChange={(e) => onUpdate({ description: e.target.value })}
                value={dataObj.description || ''}
              />
            </div>

            {/* Type */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium d-flex align-items-center gap-2">
                <span>Type:</span>
                <span className="d-inline-flex">
                  <AlertIcons type={dataObj.type} />
                </span>
              </div>
              <select
                className="bg-base p-1 w-100"
                onChange={(e) => onUpdate({ type: e.target.value })}
                value={dataObj.type || ''}
              >
                <option value="">-Select-</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Radius */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Radius (ft):</div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  name="radius"
                  className="bg-base p-1 rounded-1"
                  style={{ width: 100 }}
                  onChange={(e) =>
                    onUpdate({
                      radius: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                  value={dataObj.radius ?? ''}
                />
                <span className="text-body-secondary">ft</span>
              </div>
            </div>

            {/* Severity */}
            <div className="d-flex flex-column">
              <div
                className="px-2 py-1 rounded-1 mb-2"
                style={{
                  width: 'max-content',
                  background: alertColorScale(dataObj.severity),
                }}
              >
                Severity: {getSeverityLevel(dataObj.severity)}
              </div>

              {/* slider in a minimal box */}
              <div className="px-2 rounded-2 bg-base" style={{ minWidth: 0 }}>
                <Slider
                  className="w-100 m-1"
                  name="severity"
                  min={0}
                  max={99}
                  step={1}
                  value={dataObj.severity ?? 25}
                  onChange={(val) => {
                    //Verify val
                    onUpdate({ severity: val });
                  }}
                  styles={{
                    track: { backgroundColor: theme.primaryHighlight },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const eventTypes = [
  '911 Telephone Outage Emergency',
  'Administrative Message',
  'Air Quality Alert',
  'Air Stagnation Advisory',
  'Arroyo And Small Stream Flood Advisory',
  'Ashfall Advisory',
  'Ashfall Warning',
  'Avalanche Advisory',
  'Avalanche Warning',
  'Avalanche Watch',
  'Beach Hazards Statement',
  'Blizzard Warning',
  'Blizzard Watch',
  'Blowing Dust Advisory',
  'Blowing Dust Warning',
  'Brisk Wind Advisory',
  'Child Abduction Emergency',
  'Civil Danger Warning',
  'Civil Emergency Message',
  'Coastal Flood Advisory',
  'Coastal Flood Statement',
  'Coastal Flood Warning',
  'Coastal Flood Watch',
  'Dense Fog Advisory',
  'Dense Smoke Advisory',
  'Dust Advisory',
  'Dust Storm Warning',
  'Earthquake Warning',
  'Evacuation - Immediate',
  'Excessive Heat Warning',
  'Excessive Heat Watch',
  'Extreme Cold Warning',
  'Extreme Cold Watch',
  'Extreme Fire Danger',
  'Extreme Wind Warning',
  'Fire Warning',
  'Fire Weather Watch',
  'Flash Flood Statement',
  'Flash Flood Warning',
  'Flash Flood Watch',
  'Flood Advisory',
  'Flood Statement',
  'Flood Warning',
  'Flood Watch',
  'Freeze Warning',
  'Freeze Watch',
  'Freezing Fog Advisory',
  'Freezing Rain Advisory',
  'Freezing Spray Advisory',
  'Frost Advisory',
  'Gale Warning',
  'Gale Watch',
  'Hard Freeze Warning',
  'Hard Freeze Watch',
  'Hazardous Materials Warning',
  'Hazardous Seas Warning',
  'Hazardous Seas Watch',
  'Hazardous Weather Outlook',
  'Heat Advisory',
  'Heavy Freezing Spray Warning',
  'Heavy Freezing Spray Watch',
  'High Surf Advisory',
  'High Surf Warning',
  'High Wind Warning',
  'High Wind Watch',
  'Hurricane Force Wind Warning',
  'Hurricane Force Wind Watch',
  'Hurricane Local Statement',
  'Hurricane Warning',
  'Hurricane Watch',
  'Hydrologic Advisory',
  'Hydrologic Outlook',
  'Ice Storm Warning',
  'Lake Effect Snow Advisory',
  'Lake Effect Snow Warning',
  'Lake Effect Snow Watch',
  'Lake Wind Advisory',
  'Lakeshore Flood Advisory',
  'Lakeshore Flood Statement',
  'Lakeshore Flood Warning',
  'Lakeshore Flood Watch',
  'Law Enforcement Warning',
  'Local Area Emergency',
  'Low Water Advisory',
  'Marine Weather Statement',
  'Nuclear Power Plant Warning',
  'Radiological Hazard Warning',
  'Red Flag Warning',
  'Rip Current Statement',
  'Severe Thunderstorm Warning',
  'Severe Thunderstorm Watch',
  'Severe Weather Statement',
  'Shelter In Place Warning',
  'Short Term Forecast',
  'Small Craft Advisory',
  'Small Craft Advisory For Hazardous Seas',
  'Small Craft Advisory For Rough Bar',
  'Small Craft Advisory For Winds',
  'Small Stream Flood Advisory',
  'Snow Squall Warning',
  'Special Marine Warning',
  'Special Weather Statement',
  'Storm Surge Warning',
  'Storm Surge Watch',
  'Storm Warning',
  'Storm Watch',
  'Test',
  'Tornado Warning',
  'Tornado Watch',
  'Tropical Depression Local Statement',
  'Tropical Storm Local Statement',
  'Tropical Storm Warning',
  'Tropical Storm Watch',
  'Tsunami Advisory',
  'Tsunami Warning',
  'Tsunami Watch',
  'Typhoon Local Statement',
  'Typhoon Warning',
  'Typhoon Watch',
  'Urban And Small Stream Flood Advisory',
  'Volcano Warning',
  'Wind Advisory',
  'Wind Chill Advisory',
  'Wind Chill Warning',
  'Wind Chill Watch',
  'Winter Storm Warning',
  'Winter Storm Watch',
  'Winter Weather Advisory',
];
